import React from 'react'
import PropTypes from 'prop-types'
import { classNames } from 'utils/utils'
import strings from 'utils/strings'
import { displayModuleName } from 'i18n/displayConstants'
import XMC from 'components/svg/XMC_Module_small'
import QPMInfo from 'components/QPMInfo'
import ASAMGraphic from 'components/svg/ASAM_Module_small'
import {
  BUS_MODULES,
  MIN_APP_VARIANT_FOR_SWITCH_INDICATOR,
} from 'utils/constants.js'
import { XMC3, XMC4 } from 'utils/constants/vehicle-constants'
import { useSelector } from 'hooks/redux/index.js'
import { getUserSetting } from 'selectors/user.js'
import USER_SETTINGS from 'constants/userSettings.js'
import { PercentCompleteLine } from 'components/patterns/Charts/index.js'
import withStyle, {
  DiagnosticVarient,
  DiagnosticVarientLabel,
  Variant,
  ModuleName,
  ModuleGraphic,
  ModuleTitleWrapper,
  ModuleNameWrapper,
  ModuleSummaryWrapper,
  ModuleMetricsWrapper,
} from './ModuleSummary.style.js'
import ModuleSummaryMetrics from './ModuleSummaryMetrics'
import ModuleSummaryMetricsPercentLineVariant from './ModuleSummaryMetricsPercentLineVariant'

const getModuleGraphic = (moduleName) => {
  if (moduleName.includes('ASAM')) return <ASAMGraphic />

  if (moduleName.includes('XMC')) return <XMC />

  return null
}

const ModuleSummary = ({
  className,
  moduleName,
  variant,
  size,
  isSummaryViewExpanded,
  isSchoolBus,
  ...metricProps
}) => {
  const isModuleSummaryViewExpandedSetting = useSelector(
    getUserSetting,
    USER_SETTINGS.ENABLE_CHEC_PLUS_MODULE_SUMMARY_EXPANDED_VIEW
  )
  const isModuleSummaryViewExpanded =
    typeof isModuleSummaryViewExpandedSetting === 'boolean'
      ? isModuleSummaryViewExpandedSetting
      : true

  const showGraphic = ['LARGE', 'XLARGE'].includes(size)
  const ModuleMetricsComponent = isSummaryViewExpanded
    ? ModuleSummaryMetricsPercentLineVariant
    : ModuleSummaryMetrics

  const showQPMInfo =
    (moduleName === XMC3 || moduleName === XMC4) &&
    !isSchoolBus &&
    isModuleSummaryViewExpanded
  let qpmText
  if (showQPMInfo) {
    qpmText = moduleName === XMC3 ? strings.qpm1 : strings.qpm2
  }

  const isCompact = isSummaryViewExpanded

  const variantParts = variant ? variant.split('_') : []
  const diagnosticVariantStringNumber =
    variantParts.length > 1 ? variantParts[1] : ''
  const diagVarHexNumber = parseInt(diagnosticVariantStringNumber, 16)
  const isValidVariantSwichtInd =
    !Number.isNaN(diagVarHexNumber) &&
    diagVarHexNumber >= MIN_APP_VARIANT_FOR_SWITCH_INDICATOR
  const showSwitchIndicator =
    isValidVariantSwichtInd && BUS_MODULES.includes(moduleName)

  return (
    <ModuleSummaryWrapper compact={isCompact}>
      <ModuleTitleWrapper compact={isCompact}>
        <ModuleNameWrapper compact={isCompact}>
          <ModuleName compact={isCompact} weight="bold">
            {displayModuleName(moduleName)}
          </ModuleName>
          <Variant>
            <DiagnosticVarientLabel compact={isCompact}>
              {strings.diagnostic_variant}: &nbsp;
            </DiagnosticVarientLabel>
            <DiagnosticVarient compact={isCompact}>{variant}</DiagnosticVarient>
          </Variant>
        </ModuleNameWrapper>
        {showQPMInfo && <QPMInfo qpmText={qpmText} compact={isCompact} />}
      </ModuleTitleWrapper>
      <div
        className={classNames(className, 'chec-plus-vehicle-module-summary')}
      >
        {!isCompact && (
          <PercentCompleteLine
            label={strings.pins}
            count={metricProps.pinCount}
            total={metricProps.maxPins}
            id={`${moduleName}-pins`}
          />
        )}
        <ModuleMetricsWrapper compact={isCompact}>
          {showGraphic && (
            <ModuleGraphic>{getModuleGraphic(moduleName)}</ModuleGraphic>
          )}
          <ModuleMetricsComponent
            {...metricProps}
            moduleName={moduleName}
            showSwitchIndicator={showSwitchIndicator}
            showCanInputs={BUS_MODULES.includes(moduleName)}
          />
        </ModuleMetricsWrapper>
      </div>
    </ModuleSummaryWrapper>
  )
}

ModuleSummary.propTypes = {
  /** Make sure to comment */
  /** Classname */
  className: PropTypes.string,
  /** isSummaryViewExpanded */
  isSummaryViewExpanded: PropTypes.bool,
  /** Name of the module (e.g. XMC/2) */
  moduleName: PropTypes.string,
  /** number of logicsets used by all feaures using this module on the vehicle */
  logicCount: PropTypes.number,
  /** max logicsets that can be used in total by all feaures using this module on the vehicle */
  maxLogics: PropTypes.number,
  /** number of timers used by all feaures using this module on the vehicle */
  timerCount: PropTypes.number,
  /** max timers that can be used in total by all feaures using this module on the vehicle */
  maxTimers: PropTypes.number,
  /** number of pins used by all feaures using this module on the vehicle */
  pinCount: PropTypes.number,
  /** max pins that can be used in total by all feaures using this module on the vehicle */
  maxPins: PropTypes.number,
  /** Navigate to the ecu page with module selected */
  goToEcu: PropTypes.func,
  /** current software variant applied to the module */
  variant: PropTypes.string,
  size: PropTypes.string,
  isSchoolBus: PropTypes.bool,
}

export default withStyle(ModuleSummary)
